// TK-6 — the Task float: A.1's list lifted into a box that stays on top of
// every page. Opened from A.1, docked by Dock, the ×, or a new session.
// TK-15 — Pop out puts the same list in its own window, which can be dragged
// to a second screen; a page element never could. Drag, fold and dock are as
// they were.
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { getCurrentUserId } from '../../utils/currentUser';
import { fmtMinutes, plannedMinutes } from '../../utils/taskLists';
import TaskListBody, { TaskSheet } from './TaskListBody';
import { useTaskList } from './useTaskList';
import '@/styles/tasks-page.css';

const BOX = (me) => `kmt-float:${me || 'anon'}`;
const OPEN = (me) => `kmt-float-open:${me || 'anon'}`;
const POP = (me) => `kmt-pop:${me || 'anon'}`;

const readBox = (me) => {
  const fallback = { x: Math.max(16, window.innerWidth - 360 - 24), y: 96, w: 360, h: 520, folded: false };
  try {
    const v = JSON.parse(window.localStorage.getItem(BOX(me)) || 'null');
    return v && Number.isFinite(v.x) ? { ...fallback, ...v } : fallback;
  } catch { return fallback; }
};
const writeBox = (me, v) => {
  try { window.localStorage.setItem(BOX(me), JSON.stringify(v)); } catch { /* private mode */ }
};

// TK-15 — where the popped window was left, in screen coordinates, so the next
// Pop out lands on the same monitor and the same spot.
const readPop = (me) => {
  try {
    const v = JSON.parse(window.localStorage.getItem(POP(me)) || 'null');
    return v && Number.isFinite(v.left) && Number.isFinite(v.width) ? v : null;
  } catch { return null; }
};
const writePop = (me, v) => {
  try { window.localStorage.setItem(POP(me), JSON.stringify(v)); } catch { /* private mode */ }
};

// A phone has no second screen, and a finger cannot drag a window.
const canPopOut = () => {
  try {
    if (typeof window.matchMedia === 'function') {
      return window.matchMedia('(min-width: 768px) and (pointer: fine)').matches;
    }
  } catch { /* older browser */ }
  return window.innerWidth >= 768;
};

// MD-1 — the float lives INSIDE the app frame, clear of the right tab rail.
// It used to be clamped to the window, so an open float crossed the frame's
// right edge and sat over the rail's tabs.
const clampBox = (b) => {
  const el = document.querySelector('.km-shell__frame');
  const frame = el ? el.getBoundingClientRect()
    : { left: 0, top: 0, right: window.innerWidth, bottom: window.innerHeight };
  const rail = parseFloat(getComputedStyle(document.documentElement)
    .getPropertyValue('--km-tabrail')) || 28;
  const right = frame.right - rail;
  const w = b.folded ? 0 : (Number(b.w) || 0);
  const h = b.folded ? 0 : (Number(b.h) || 0);
  const x = Math.max(frame.left, Math.min(b.x, Math.max(frame.left, right - (w || 48))));
  const y = Math.max(frame.top, Math.min(b.y, Math.max(frame.top, frame.bottom - (h || 48))));
  return { ...b, x, y };
};

export default function TaskFloat() {
  const me = getCurrentUserId();
  const navigate = useNavigate();
  const [open, setOpen] = useState(() => {
    try { return window.sessionStorage.getItem(OPEN(me)) === '1'; } catch { return false; }
  });
  const [box, setBox] = useState(() => clampBox(readBox(me)));
  const [popped, setPopped] = useState(false);
  const ref = useRef(null);
  const dragRef = useRef(null);
  const popRef = useRef(null);

  // The list is asleep while the float is docked, and while it is popped out
  // the other window is the one doing the reading.
  const list = useTaskList({ me, active: open && !popped });

  useEffect(() => {
    const show = () => {
      setOpen(true);
      // MD-1 — the frame exists by the time the float is asked for, so clamp
      // here too: at mount it may not, and the window fallback is wider.
      setBox(b => { const n = clampBox(readBox(me)); writeBox(me, n); return { ...b, ...n }; });
      try { window.sessionStorage.setItem(OPEN(me), '1'); } catch { /* private mode */ }
    };
    // TK-13 — a task sheet opening on a phone asks the float to get out of the way.
    const foldAway = () => setBox(b => {
      if (b.folded) return b;
      const n = clampBox({ ...b, folded: true });
      writeBox(me, n);
      return n;
    });
    window.addEventListener('open-task-float', show);
    window.addEventListener('fold-task-float', foldAway);
    return () => {
      window.removeEventListener('open-task-float', show);
      window.removeEventListener('fold-task-float', foldAway);
    };
  }, [me]);

  // The box remembers where it was left and how wide it was made. MD-1 — the
  // width is debounced and the box re-clamped, so widening it cannot push the
  // float past the frame.
  useEffect(() => {
    const el = ref.current;
    if (!el || box.folded || popped) return undefined;
    let timer = 0;
    const ro = new ResizeObserver(() => {
      const w = Math.max(260, Math.round(el.offsetWidth));
      const h = Math.max(240, Math.round(el.offsetHeight));
      if (w === box.w && h === box.h) return;
      window.clearTimeout(timer);
      timer = window.setTimeout(() => {
        setBox(b => { const n = clampBox({ ...b, w, h }); writeBox(me, n); return n; });
      }, 300);
    });
    ro.observe(el);
    return () => { window.clearTimeout(timer); ro.disconnect(); };
  }, [open, box.folded, box.w, box.h, me, popped]);

  // MD-1 — a narrower window, or the rail appearing, brings the float back in.
  useEffect(() => {
    if (!open) return undefined;
    const back = () => setBox(b => { const n = clampBox(b); writeBox(me, n); return n; });
    window.addEventListener('resize', back);
    return () => window.removeEventListener('resize', back);
  }, [open, me]);

  useEffect(() => {
    const move = (e) => {
      const d = dragRef.current;
      if (!d) return;
      setBox(b => clampBox({ ...b, x: e.clientX - d.dx, y: e.clientY - d.dy }));
    };
    const up = () => {
      if (!dragRef.current) return;
      dragRef.current = null;
      setBox(b => { const n = clampBox(b); writeBox(me, n); return n; });
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    return () => { window.removeEventListener('pointermove', move); window.removeEventListener('pointerup', up); };
  }, [me]);

  // TK-15 — while the list is in its own window: watch for it closing, and keep
  // a note of where the user left it.
  useEffect(() => {
    if (!popped) return undefined;
    const heard = (e) => {
      if (e?.data?.kind === 'tasks-window-closed') { popRef.current = null; setPopped(false); }
    };
    window.addEventListener('message', heard);
    const id = setInterval(() => {
      const w = popRef.current;
      if (!w || w.closed) { popRef.current = null; setPopped(false); return; }
      try {
        writePop(me, {
          left: Math.round(w.screenX), top: Math.round(w.screenY),
          width: Math.round(w.outerWidth) || 380, height: Math.round(w.outerHeight) || 720,
        });
      } catch { /* the window is on its way out */ }
    }, 1000);
    return () => { window.removeEventListener('message', heard); clearInterval(id); };
  }, [popped, me]);

  if (!open) return null;

  const startDrag = (e) => {
    if (e.target.closest('button')) return;
    if (window.innerWidth <= 767) return;
    dragRef.current = { dx: e.clientX - box.x, dy: e.clientY - box.y };
  };
  const dock = () => {
    setOpen(false);
    try { window.sessionStorage.removeItem(OPEN(me)); } catch { /* private mode */ }
  };
  const fold = () => setBox(b => { const n = clampBox({ ...b, folded: !b.folded }); writeBox(me, n); return n; });

  const popOut = () => {
    const spot = readPop(me) || {
      left: Math.round((window.screenX || 0) + box.x),
      top: Math.round((window.screenY || 0) + box.y),
      width: 380, height: 720,
    };
    const win = window.open('/widget/tasks', 'keymade-tasks',
      `width=${spot.width},height=${spot.height},left=${spot.left},top=${spot.top}`);
    if (!win) { toast.error('Your browser blocked the window'); return; }
    writePop(me, spot);
    popRef.current = win;
    setPopped(true);
  };
  const bringBack = () => {
    try { if (popRef.current && !popRef.current.closed) popRef.current.close(); }
    catch { /* already gone */ }
    popRef.current = null;
    setPopped(false);
  };

  return (
    <>
    <aside ref={ref} className={`kmt-float${box.folded ? ' is-folded' : ''}${popped ? ' is-popped' : ''}`}
      style={{ left: box.x, top: box.y, width: box.w, height: box.h }} data-testid="task-float">
      <header className="kmt-float__hd" onPointerDown={startDrag} data-testid="task-float-handle">
        <span className="kmt-float__k">
          ⠿ Today · {list.doneCount} of {list.plan.length + list.restDone.length} · {fmtMinutes(plannedMinutes([...list.three, ...list.rest])) || '0 m'}
        </span>
        <span className="kmt-float__acts">
          {canPopOut() && !popped && (
            <button type="button" className="kmt-chip" onClick={popOut} data-testid="task-float-popout">pop out</button>
          )}
          <button type="button" className="kmt-chip" onClick={fold} data-testid="task-float-fold">{box.folded ? 'open' : 'fold'}</button>
          <button type="button" className="kmt-chip" onClick={dock} data-testid="task-float-dock">dock</button>
          <button type="button" className="kmt-chip" onClick={dock} aria-label="Close" data-testid="task-float-close">×</button>
        </span>
      </header>
      {popped ? (
        <p className="kmt-popped" data-testid="task-float-popped">
          Open in its own window ·
          <button type="button" className="kmt-float__go" onClick={bringBack}
            data-testid="task-float-bring-back">Bring back</button>
        </p>
      ) : !box.folded && (
        <TaskListBody list={list} prefix="task-float"
          onGo={() => navigate('/dashboard?tab=tasks')} />
      )}
    </aside>
    <TaskSheet list={list} />
    </>
  );
}
